/**
 * Where an attack–decay–sustain envelope has got to at a given moment.
 *
 * A release has to start from the level the envelope has reached, and the
 * obvious way to find it — reading the gain's `value` — reads the value *now*,
 * not at the release time. Everything an instrument schedules is scheduled
 * ahead: by the lookahead when playing live, by the whole piece when rendering
 * offline. Read at scheduling time, an offline render's every note was still at
 * zero, and its release was a hard cut. The envelope is three straight lines,
 * so the level at any time is arithmetic.
 */
export interface EnvelopeShape {
  /** Context time the note started. */
  startedAt: number;
  attack: number;
  decay: number;
  peak: number;
  sustain: number;
  /**
   * The release once it has been scheduled: a straight line from `from` at
   * `at` to zero at `end`. A voice let go twice — released and then choked,
   * or stopped with everything else — must start its second ramp from where
   * the first had got to, not from the level it was at before either.
   */
  release?: { at: number; from: number; end: number };
}

export function envelopeLevelAt(env: EnvelopeShape, when: number): number {
  const release = env.release;
  if (release && when >= release.at) {
    if (when >= release.end) return 0;
    return release.from * (1 - (when - release.at) / (release.end - release.at));
  }
  const t = when - env.startedAt;
  if (t <= 0) return 0;
  if (t < env.attack) return (env.peak * t) / env.attack;
  const intoDecay = t - env.attack;
  if (intoDecay < env.decay) return env.peak + ((env.sustain - env.peak) * intoDecay) / env.decay;
  return env.sustain;
}
